import { useQueryClient } from "@tanstack/react-query";

import { realtimeClient } from "@/features/realtime";
import { TENANT_ID_STORAGE_KEY } from "@/lib/contexts/Auth/AuthContext";
import { useLocalStorage } from "@/lib/hooks/useLocalStorage";
import { UserRole } from "@/types/user/userType";

/**
 * Hook to handle user logout.
 * Clears all auth-related localStorage items and clears the query client cache.
 *
 * @returns Logout function
 *
 * @example
 * ```tsx
 * const logout = useLogout();
 * logout();
 * ```
 */
export function useLogout() {
  const queryClient = useQueryClient();
  const [, , removeLocalTenantId] = useLocalStorage(TENANT_ID_STORAGE_KEY, "");
  const [, , removeUserId] = useLocalStorage("userId", "");
  const [, , removeAccessToken] = useLocalStorage("accessToken", "");
  const [, , removeRole] = useLocalStorage<UserRole | null>("role", null);

  return () => {
    void realtimeClient.disconnect();

    removeLocalTenantId();
    removeUserId();
    removeAccessToken();
    removeRole();

    localStorage.removeItem("email");
    localStorage.removeItem("firstname");
    localStorage.removeItem("infix");
    localStorage.removeItem("lastname");
    localStorage.removeItem("refreshToken");

    queryClient.clear();
  };
}
